import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { APIKey, IAPIKey, TAPIKeyScope } from '@feathr/blackbox';
import { APIKeyServiceNames } from '@feathr/blackbox';
import { showError, submitModelWithErrorHandling } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { selectedScopesToArray } from './ScopePicker/ScopePicker.utils';

export interface IUseCreateAPIKeyOptions {
  /** Service (Feathr API) success — hands off the one-time secret to the secret step. */
  onServiceKeyCreated: (secret: string) => void;
  /** Legacy (Zapier) success — the key is recoverable from the list, so just close. */
  onLegacyKeyCreated: () => void;
  /** Expiry for the default preset, so leaving the default still sends expires_at. */
  initialExpiresAt?: string;
}

export interface IUseCreateAPIKey {
  serviceName: APIKeyServiceNames;
  name: string;
  selectedScopes: Set<TAPIKeyScope>;
  isLegacy: boolean;
  canCreate: boolean;
  isCreating: boolean;
  setServiceName: (serviceName: APIKeyServiceNames) => void;
  setName: (name: string) => void;
  setScopes: (scopes: Set<TAPIKeyScope>) => void;
  setExpiresAt: (expiresAt: string | undefined) => void;
  setIsExpirationComplete: (isComplete: boolean) => void;
  handleCreate: () => Promise<void>;
  /** Restore every field to its initial state (used when the modal reopens). */
  reset: () => void;
}

/** Create-form state + create wiring for a V2 API key — Feathr API (service) or Zapier (legacy). */
export function useCreateAPIKey({
  onServiceKeyCreated,
  onLegacyKeyCreated,
  initialExpiresAt,
}: IUseCreateAPIKeyOptions): IUseCreateAPIKey {
  const { t } = useTranslation();
  const { APIKeys } = useStore();

  const [serviceName, setServiceName] = useState<APIKeyServiceNames>(APIKeyServiceNames.FeathrAPI);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Set<TAPIKeyScope>>(new Set<TAPIKeyScope>());
  const [expiresAt, setExpiresAt] = useState<string | undefined>(initialExpiresAt);
  const [isCreating, setIsCreating] = useState(false);
  // False for "Custom date" until a date is picked, so create can't fire a never-expiring key.
  const [isExpirationComplete, setIsExpirationComplete] = useState(true);

  // Zapier issues a legacy JWT key: scopeless, no expiry — so only a name is required.
  const isLegacy = serviceName === APIKeyServiceNames.Zapier;
  const canCreate =
    name.trim().length > 0 && (isLegacy || (selectedScopes.size > 0 && isExpirationComplete));

  async function handleCreate(): Promise<void> {
    if (!canCreate) {
      return;
    }

    // `service_name` is the user's key name; the service maps to `kind`. Cast
    // because blackbox types service_name as the narrower enum (widen in #8547).
    const attributes: Partial<IAPIKey> = isLegacy
      ? { kind: 'legacy', service_name: name.trim() as APIKeyServiceNames }
      : {
          kind: 'service',
          scopes: selectedScopesToArray(selectedScopes),
          service_name: name.trim() as APIKeyServiceNames,
        };
    if (!isLegacy && expiresAt !== undefined) {
      attributes.expires_at = expiresAt;
    }

    const model = APIKeys.create(attributes);

    setIsCreating(true);
    await submitModelWithErrorHandling<APIKey>({
      onSuccess: (created) => {
        if (isLegacy) {
          // Legacy key is recoverable from the list; nothing to hand off.
          onLegacyKeyCreated();
          return;
        }
        const secret = created.get('secret');
        if (secret) {
          onServiceKeyCreated(secret);
        } else {
          // Secret is returned only on create; a 200 without it can't be recovered.
          showError(
            new Error(t('The key was created but its secret was not returned. Please try again.')),
            t,
          );
        }
      },
      operation: APIKeys.add(model),
      t,
    });
    setIsCreating(false);
  }

  // Stable so a reopen effect can depend on it without refiring every render.
  const reset = useCallback((): void => {
    setServiceName(APIKeyServiceNames.FeathrAPI);
    setName('');
    setSelectedScopes(new Set<TAPIKeyScope>());
    setExpiresAt(initialExpiresAt);
    setIsExpirationComplete(true);
  }, [initialExpiresAt]);

  return {
    canCreate,
    handleCreate,
    isCreating,
    isLegacy,
    name,
    selectedScopes,
    serviceName,
    setExpiresAt,
    setIsExpirationComplete,
    setName,
    setScopes: setSelectedScopes,
    setServiceName,
    reset,
  };
}
